<!-- eslint-disable vue/no-v-html -->
<template>
  <div>
    <div>
      <ProgressBar active-step="Take a moment to review and confirm your information" />
      <PageHeading :default-heading="pageHeader" />
      <form @submit.prevent>
        <!-- Account details -->
        <div class="pvd-container container-divider">
          <h2 class="pvd-heading-L">
            {{ pd_content.account_details_header }}
          </h2>
          <div class="pvd-display-row">
            <div class="display-row-label">
              <p>{{ pd_content.account_type_label }}</p>
            </div>
            <div class="display-row-content">
              <p>{{ pd_content.account_type }}</p>
            </div>
          </div>
          <template v-if="youth">
            <div>
              <div class="pvd-display-row">
                <div class="display-row-label">
                  <p>
                    {{ pd_content.rc_dbt_atm_card.section_label }}
                  </p>
                </div>
                <div class="display-row-content">
                  <p>
                    {{ pd_content.rc_dbt_atm_card.section_value }}
                  </p>
                </div>
              </div>
            </div>
          </template>
        </div>
        <!-- Personal information details -->
        <div class="pvd-container container-divider">
          <h2 class="pvd-heading-L">
            {{ pd_content.edit_personal_information_header }}
            <EditLink
              v-if="!ShowTaxDisclousureAndNotice"
              page-name="personal-information"
              is-not-istock="true"
            />
          </h2>
          <apx-full-name
            :group-label="pd_content.customer_info_label.name"
            :first-name="pu_firstName"
            :middle-name="pu_middleName"
            :last-name="pu_lastName"
            readonly
          />
          <apx-ssn
            :label="pd_content.customer_info_label.social"
            :value="showSecurityNumber"
            mask-regex="\d\d\d-\d\d-"
            :mask-pattern="maskSsn"
            readonly
          />
          <apx-date-select
            :group-label="pd_content.customer_info_label.dob"
            :date-input-value="pu_dob"
            readonly
          />
          <apx-phone
            :telephone-number-label="pd_content.customer_info_label.phonenumber"
            :checked="pu_mobilePhoneCheckIndicator"
            :telephone-number="pu_dayPhone"
            readonly
          />
          <apx-email
            :label="pd_content.customer_info_label.email"
            :value="pu_email"
            readonly
          />
          <!-- Promotional code details -->
          <ReadbackComponent
            v-if="showOffers"
            id="promotionalReadback"
            :label="pd_content.rc_promotional.rc_offer_header"
            :values="[pu_promotionalText]"
            show-help
            :show-anchor="showOfferAnchor"
            :anchor-value="pd_content.rc_promotional.rc_offer_link"
            :help-body="pd_content.rc_promotional.rc_offer_modal_body"
            :help-header="pd_content.rc_promotional.rc_offer_header"
            page-name="personal-information"
          />
          <apx-select
            :value="residenceCountryName"
            :label="pd_content.country_of_citizenship_label"
            readonly
          />
          <ReadbackComponent
            :label="pd_content.residential_address_label"
            :values="[pu_leg_line1, pu_leg_line2, cityStateZip]"
          />
        </div>
        <!-- Employement information details -->
        <div class="pvd-container container-divider">
          <h2 class="pvd-heading-L">
            {{ pd_content.rc_employement_section.ei_section_header }}
            <EditLink
              page-name="employment-information"
              is-not-istock="true"
            />
          </h2>
          <apx-select
            :value="pd_content.employment_status_mapping[pu_employmentStatus]"
            :label="pd_content.rc_employement_section.ei_section_employment_status"
            readonly
          />
          <apx-text-input
            v-if="showEmployerInfo"
            :value="pu_occupation? pu_occupation : pd_content.none_value_text"
            :label="pd_content.rc_employement_section.ei_section_occupation"
            readonly
          />
          <apx-text-input
            v-if="showEmployerInfo"
            :value="pu_employersName? pu_employersName : pd_content.none_value_text"
            :label="pd_content.rc_employement_section.ei_section_company_name2"
            readonly
          />
          <ReadbackComponent
            v-if="showEmployerInfo"
            :label="pd_content.rc_employement_section.ei_section_company_address"
            :values="employerAddress"
          />
          <ReadbackComponent
            v-if="pu_employmentStatus != 'Z'"
            :id="'associated-company-info'"
            :label="pd_content.rc_employement_section.ei_section_company_name"
            :values="pu_coAffillName ? [pu_coAffillName] : [pd_content.none_value_text]"
            show-help
            :help-header="pd_content.rc_employement_section.ei_section_company_name_help_header"
            :help-body="pd_content.rc_employement_section.ei_section_company_name_help_body"
          />
          <ReadbackComponent
            :id="'corporate-control-status'"
            :label="pd_content.rc_employement_section.ei_section_control_status"
            :values="pu_coDirectorSymbol ? [pu_coDirectorSymbol] : [pd_content.none_value_text]"
            show-help
            :help-header="pd_content.rc_employement_section.ei_section_control_status_help_header"
            :help-body="pd_content.rc_employement_section.ei_section_control_status_help_body"
          />
        </div>
        <!-- Joint information details -->
        <SecondaryUserDetailsComponent v-if="isManagedAccount && isJoint" />
        <!-- Strategic Advisers LLC details -->
        <StrategicAdviserProxyComponent
          v-if="isManagedAccount"
          :show-sai="showSAI"
        />
        <div
          v-if="ShowTaxDisclousureAndNotice"
          class="pvd-container container-divider"
        >
          <h2 class="pvd-heading-L">
            {{ pd_content.rc_tax_withholding.tax_certification_header }}
          </h2>
          <apx-toggle
            id="toggleTax"
            v-observe-visibility="taxToggleBecameVisible"
            :value="isToggleTax"
            :yes-label="pd_content.rc_tax_withholding.toggle_text_yes"
            :no-label="pd_content.rc_tax_withholding.toggle_text_no"
            :group-label="pd_content.rc_tax_withholding.toggle_label"
            :track-events="true"
            stack
          />
          <p
            v-if="isToggleTax === 'Yes'"
            class="xsmall"
          >
            {{ pd_content.rc_tax_withholding.tax_certification_paragraph }}
          </p>
        </div>
        <!-- Youth information details -->
        <SecondaryUserDetailsComponent
          v-if="youth"
          :prospectus-url="prospectusUrl"
        />
        <!-- Before you open your account -->
        <div
          v-if="!youth"
          class="pvd-container container-divider"
        >
          <h2
            id="openAcctHeader"
            class="pvd-heading-L"
          >
            {{ pd_content.rc_open_your_account_header }}
          </h2>
          <!-- Ledge 1 -->
          <p class="m-small-top">
            {{ pd_content.rc_paragraph_lede }}
          </p>
          <ul class="pvd-list-bullet">
            <li>
              <a
                class="pvd-anchor"
                href="#"
                @click.prevent="handleCustomerAgreementClick()"
              > {{ CustAgreementText.value
              }}</a>{{ CustAgreementText.desc }}
            </li>
            <li v-if="rollover">
              <a
                class="pvd-anchor"
                href="#"
                @click.prevent="handleCorePositionAgreementClick()"
              > {{
                pd_content.rc_core_position_agreement.spaxx_url_label }}</a> {{ pd_content.rc_core_position_agreement.spaxx_url_desc }}
            </li>
            <li v-if="isManagedAccount">
              <a
                class="pvd-anchor"
                href="#"
                @click.prevent="handleBrochureSupplementClick()"
              > {{ BrochureSupplementText
              }}</a>
            </li>
            <li>
              <a
                class="pvd-anchor"
                href="#"
                @click.prevent="handleTermsConditionClick()"
              > {{ TermsConditionText }}</a>
              {{ pd_content.rc_terms_and_conditions_pdf.text }}
            </li>
            <li v-if="isBrokerageFlow">
              <a
                class="pvd-anchor"
                href="#"
                @click.prevent="handleTrustedContactClick()"
              > {{ TrustedContactText
              }}</a>{{ pd_content.rc_trusted_contact_pdf
                ? pd_content.rc_trusted_contact_pdf.text : '' }}
            </li>
            <li v-if="isBrokerageFlow">
              <a
                class="pvd-anchor"
                href="#"
                @click.prevent="handleHouseholdingOfShareholderClick()"
              > {{
                HouseholdingOfShareholderText }}</a>{{ pd_content.rc_householding_of_shareholder_pdf
                ? pd_content.rc_householding_of_shareholder_pdf.text : '' }}
            </li>
            <li>
              <a
                id="eDeliveryChecked"
                class="pvd-anchor"
                href="#"
                @click.prevent="handleElectronicDelivery()"
              > {{
                ElectronicDeliveryText }}</a> {{ pd_content.rc_electronic_delivery_agreement_pdf.text }}
            </li>
            <li v-if="prospectusUrl && showManagedProspectusesLink">
              <a
                id="prospectus-modal-trigger"
                class="pvd-anchor"
                href="javascript:void(0);"
                @click="handleManagedProspectusClick()"
              > {{ pd_content.rc_prospectuses }}
              </a>
            </li>

            <ModalComponent
              v-if="showManagedProspectusesLink && pd_prospectuses.models.length > 0"
              :modal-header="pd_content.rc_prospectuses"
              :modal-list-link="ManagedProspectus"
              body-class="xlarge m-xxsmall-top"
              modal-class="small"
              id-val="prospectus-modal"
              @exitmodal-action="openProspectusWindow"
            />
            <ModalComponent
              v-if="isManagedAccount && showManagedProspectusesLink && pd_prospectuses.models.length === 0"
              :modal-body1="pd_content.rc_prospectus_error_message"
              body-class="xlarge m-xxsmall-top"
              modal-class="small"
              id-val="prospectus-modal"
              @exitmodal-action="openProspectusWindow"
            />
          </ul>

          <!-- Ledge 2 -->
          <!--   TODO Please modify v-if for managed account -->
          <template v-if="rollover">
            <p class="m-small-top">
              {{ pd_content.rc_agreements_lede2.heading }}
              you:
            </p>
            <ul class="pvd-list-bullet">
              <li>
                {{ pd_content.rc_agreements_lede2.para1 }}
              </li>
              <li>
                {{ pd_content.rc_core_position_agreement.spaxx_agreement }}
              </li>
              <li>
                {{ pd_content.rc_agreements_lede2.para3 }}
              </li>
              <li>
                {{ pd_content.rc_agreements_lede2.para4 }}
              </li>
              <li>
                {{ pd_content.rc_agreements_lede2.para5 }}
              </li>
              <li>
                <strong>
                  {{ pd_content.rc_agreements_lede2.para6 }}
                </strong>
              </li>
            </ul>
          </template>
          <p v-if="!youth && !rollover && !isManagedAccount">
            <strong>
              {{ pd_content.rc_predispute_text }}
              <a
                class="pvd-anchor"
                href="#"
                @click.prevent="handlePredisputeArbitration()"
              >{{ PredisputeArbitrationText
              }}</a>{{ pd_content.rc_predispute_text_1 }}
            </strong>
          </p>
          <p v-if="!rollover">
            <VueShowdown
              :extensions="[formatMarkdown]"
              :markdown="pd_content.rc_open_your_account_paragragh"
            />
          </p>
          <p
            v-if="ShowTaxDisclousureAndNotice && !isManagedAccount"
            class="text-bold text-italic"
          >
            {{ pd_content.w9_certification_consent_notice }}
          </p>
        </div>
        <NavigationComponent @open-account-button-clicked="setOpenAccountButtonClicked" />
        <YouthDisclosureComponent v-if="youth" />
        <p class="rc-footnote">
          {{ pd_content.rc_footnote }}
        </p>
      </form>
    </div>
    <div
      v-if="routeId === 'managed'"
      class="pd-spray-details"
    >
      <p
        v-if="(pd_pageData.tStoreResponse && pd_pageData.tStoreResponse.params && pd_pageData.tStoreResponse.params.accountNumber !== '' &&
          pd_pageData.tStoreResponse.params.accountNumber !== undefined)"
      >
        Pregen Account Number: {{ pd_pageData.tStoreResponse.params.accountNumber }}
      </p>
      <p>
        PAS ID: {{ generatePASId }}
      </p>
      <p>Model code: {{ pd_pageData.tStoreResponse.params.modelCode }}</p>
      <p>RO Initials: {{ !pd_pageData.tStoreResponse.params.smaStrategyCd ? pd_pageData.tStoreResponse.params.repInitials : 'ZZZ' }}</p>
      <p>{{ getEReviewNum }}</p>
      <br>
      <br>
    </div>
  </div>
</template>

<script>
import EditLink from '../components/EditLink.vue';
import NavigationComponent from '../components/NavigationComponent.vue';
import ReadbackComponent from '../components/ReadbackComponent.vue';
import PageHeading from '../components/PageHeading.vue';
import ProgressBar from '../components/ProgressBar.vue';
import dataHelper from '../mixins/dataHelper';
import measurement from '../mixins/measurement';
import utilityFunctions from '../mixins/utility';
import constants from '../mixins/constants';
import SecondaryUserDetailsComponent from '../components/SecondaryUserDetailsComponent.vue';
import YouthDisclosureComponent from '../components/YouthDisclosureComponent.vue';
import StrategicAdviserProxyComponent from '../components/StrategicAdviserProxyComponent.vue';
import ModalComponent from '../components/ModalComponent.vue';

export default {
  name: 'ReviewAndConfirm',
  components: {
    NavigationComponent,
    ProgressBar,
    PageHeading,
    ReadbackComponent,
    EditLink,
    SecondaryUserDetailsComponent,
    YouthDisclosureComponent,
    StrategicAdviserProxyComponent,
    ModalComponent,
  },
  mixins: [dataHelper, measurement, utilityFunctions],
  data() {
    return {
      openAccountButtonClicked: false,
      backButtonAttempt: false,
      isToggleTax: 'No',
    };
  },
  computed: {
    generatePASId() {
      return ((this.pd_pageData.tStoreResponse
              && this.pd_pageData.tStoreResponse.params
              && this.pd_pageData.tStoreResponse.params.pasAccountId !== ''
              && this.pd_pageData.tStoreResponse.params.pasAccountId !== null
              && this.pd_pageData.tStoreResponse.params.pasAccountId !== undefined)
        ? this.pd_pageData.tStoreResponse.params.pasAccountId : this.pd_content.rc_add_spray_details);
    },
    computedCashPositions() {
      const cashPositions = [...(this.pd_cashPositions || [])];
      if (cashPositions.length > 0) {
        const defaultIndex = cashPositions.findIndex((cashPosition) => cashPosition.isDefault);
        const defaultSelected = cashPositions[defaultIndex];
        cashPositions.splice(defaultIndex, 1);
        cashPositions.splice(0, 0, defaultSelected);
        if (this.pu_cashPosition.length === 0) {
          this.commitPrimaryUserData(defaultSelected.symbol, 'cashPosition');
          this.commitSecondaryUserData(defaultSelected.symbol, 'cashPosition');
        }
      }
      return cashPositions;
    },
    prospectusUrl() {
      const cashPositionObj = this.computedCashPositions.find((o) => o.symbol === this.pu_cashPosition);
      if (cashPositionObj && cashPositionObj.prospectusURL && this.pu_cashPosition) {
        return cashPositionObj.prospectusURL;
      }
      return '';
    },

    pageHeader() {
      if (this.pd_content.page_header) {
        document.title = this.setTitle(this.pd_content.page_header);
      }
      return this.pd_content.page_header;
    },
    CustAgreementText() {
      const custagreement = {};
      custagreement.value = this.pd_content.rc_customer_agreements_pdf
        ? this.pd_content.rc_customer_agreements_pdf.value : '';
      custagreement.desc = ` ${this.pd_content.rc_bullet_one_text}`;
      return custagreement;
    },
    TermsConditionText() {
      return (this.pd_content.rc_terms_and_conditions_pdf
        ? this.pd_content.rc_terms_and_conditions_pdf.value : '');
    },
    BrochureSupplementText() {
      return (this.pd_content.rc_brochure_supplement_pdf
        ? this.pd_content.rc_brochure_supplement_pdf.value : '');
    },
    TrustedContactText() {
      return (this.pd_content.rc_trusted_contact_pdf
        ? this.pd_content.rc_trusted_contact_pdf.value : '');
    },
    HouseholdingOfShareholderText() {
      return (this.pd_content.rc_householding_of_shareholder_pdf
        ? this.pd_content.rc_householding_of_shareholder_pdf.value : '');
    },
    ElectronicDeliveryText() {
      return (this.pd_content.rc_electronic_delivery_agreement_pdf
        ? this.pd_content.rc_electronic_delivery_agreement_pdf.value : '');
    },

    PredisputeArbitrationText() {
      return (this.pd_content.rc_predispute_arbitration_clause_pdf
        ? this.pd_content.rc_predispute_arbitration_clause_pdf.value : '');
    },
    showEmployerInfo() {
      return this.pu_employmentStatus !== 'N' && this.pu_employmentStatus !== 'R';
    },
    employerAddress() {
      return (this.pu_employerAddressSameAsLegal === 'Yes' && this.pu_employmentStatus === 'S')
        ? [this.pu_leg_line1, this.pu_leg_line2, this.cityStateZip] : [
          this.pu_emp_line1, this.pu_emp_line2, this.employerCityStateZip];
    },
    CorePositionAlt() {
      return this.routeId === 'fcma';
    },
    ShowTaxDisclousureAndNotice() {
      if (this.isManaged(this.routeId)) {
        switch (this.pd_pageData.tStoreResponse.params.regType) {
          case 'IRA':
          case 'IRRL':
          case 'IS':
          case 'ROTH':
            return false;
          default:
            return true;
        }
      }
      return this.routeId === 'brokerage' || this.routeId === 'fcma';
    },
    ProgramBankText() {
      return (this.pd_content.rc_core_position_program_bank_pdf
        ? this.pd_content.rc_core_position_program_bank_pdf.value : '');
    },
    FdicText() {
      return (this.pd_content.rc_core_position_fdic_pdf
        ? this.pd_content.rc_core_position_fdic_pdf.value : '');
    },
    residenceCountryName() {
      let countryValue;
      if (this.pu_residenceCountry !== '999') {
        const countryObject = this.pd_countryList.validCitizenship
          .find((element) => element.value === this.pu_residenceCountry);
        countryValue = countryObject.option;
      } else {
        countryValue = 'UNITED STATES';
      }
      return countryValue;
    },
    cityStateZip() {
      let separator = ',';
      if (this.pu_leg_state === '' && this.pu_leg_zip === '') {
        separator = '';
      }
      return `${this.pu_leg_city}${separator} ${this.pu_leg_state} ${this.pu_leg_zip}`;
    },
    employerCityStateZip() {
      let separator = ',';
      if (this.pu_emp_state === '' && this.pu_emp_zip === '') {
        separator = '';
      }
      return `${this.pu_emp_city}${separator} ${this.pu_emp_state} ${this.pu_emp_zip}`;
    },
    showDisclosure() {
      return this.pu_flowType === constants.FLOW_TYPES.EXPRESS;
    },
    ManagedProspectus() {
      const modelHoldingsName = [];
      if (this.pd_prospectuses.models && this.pd_prospectuses.models.length > 0) {
        for (let i = 0; i < this.pd_prospectuses.models[0].modelHoldings.length; i += 1) {
          modelHoldingsName.push(this.pd_prospectuses.models[0].modelHoldings[i].finstAllocation.finstId.name);
        }
        return modelHoldingsName;
      }
      return modelHoldingsName;
    },
    isManagedAccount() {
      return this.isManaged(this.routeId);
    },
    isBrokerageFlow() {
      return this.routeId === 'brokerage';
    },
    maskSsn() {
      return this.uc_isCustomer ? '*****' : '';
    },
    showSecurityNumber() {
      return this.getSecurityNumber(this.pu_gNumber, this.pu_ssn);
    },
    youth() {
      return this.routeId === 'youth';
    },
    rollover() {
      return this.routeId === 'rolloverIRA';
    },
    showManagedProspectusesLink() {
      return this.isManaged(this.routeId)
              && this.hasManagedProspectuses(this.$globalData.tStoreResponse.params.productType);
    },
    isJoint() {
      switch (this.pd_pageData.tStoreResponse.params.regType) {
        case 'J':
        case 'TIC':
        case 'JTIC':
          return true;
        default:
          return false;
      }
    },
    showOfferAnchor() {
      // eslint-disable-next-line no-unneeded-ternary
      return (!this.pu_promotionalText || this.pu_promotionalText === '') ? true : false;
    },
    showOffers() {
      return this.isPromotionalValidForRegtype(this.$dpTogglesObj.AO_KAPAO_PROMOTIONAL_CODE_TOGGLE);
    },
  },
  mounted() {
    this.track(this.cs_stateName, 'pageview');
    this.removeModalClassIfExists();
    if (this.openAccountButtonClicked === false) {
      this.configureBackButtonFunctionality(() => this.$emit('invoke-previous'));
    }
    const toggleComponent = document.querySelector('#toggleTax');
    this.commitPrimaryUserData(false, 'taxWithHolding'); // default set to false
    if (toggleComponent) {
      toggleComponent.addEventListener('value-changed',
        this.toggleChange);
      this.toggleChange({ detail: 'No' });
    }
  },
  methods: {
    setOpenAccountButtonClicked(val) {
      this.openAccountButtonClicked = val;
      this.configureBackButtonFunctionality(() => this.showBackWarning(
        this.pd_content.back_button_disabled_warning,
      ));
    },
    toggleChange(e) {
      if (e.detail) {
        this.isToggleTax = e.detail;
        const name = `${'agreement'}-confirm-${
          this.isToggleTax === 'Yes'
        }`;
        this.track(name, 'interaction');
        const value = e.detail === 'Yes';
        this.commitPrimaryUserData(value, 'taxWithHolding');
      }
    },
    taxToggleBecameVisible(isVisible) {
      if (isVisible) this.fixApexToggleAlignmment('toggleTax');
    },
    handleCustomerAgreementClick() {
      this.track('customer_agreements_link', 'interaction');
      let url;
      if (this.isManaged(this.routeId) && this.pd_pageData.tStoreResponse && this.pd_pageData.tStoreResponse.params.productType === 'MASMA' && !this.pd_pageData.tStoreResponse.params.smaStrategyCd) {
        url = this.pd_content.rc_difs_agreements_and_disclosures ? this.pd_content.rc_difs_agreements_and_disclosures.url : '';
      } else {
        url = this.pd_content.rc_customer_agreements_pdf ? this.pd_content.rc_customer_agreements_pdf.url : '';
      }
      this.openWindow(url, 'documentwindow', '1024', '500');
    },
    handleCorePositionAgreementClick() {
      this.track('fund_prospectus_link', 'interaction');
      const url = this.pd_content.rc_core_position_agreement ? this.pd_content.rc_spaxx_prospectus_pdf.url : '';
      this.openWindow(url, 'documentwindow', '1024', '500');
    },
    handleTermsConditionClick() {
      this.track('terms_and_conditions_link', 'interaction');
      const url = this.pd_content.rc_terms_and_conditions_pdf ? this.pd_content.rc_terms_and_conditions_pdf.url : '';
      this.openWindow(url, 'documentwindow', '1024', '500');
    },
    handleBrochureSupplementClick() {
      this.track('brochure_supplement_link', 'interaction');
      const url = this.pd_content.rc_brochure_supplement_pdf ? this.pd_content.rc_brochure_supplement_pdf.url : '';
      this.openWindow(url, 'documentwindow', '1024', '500');
    },
    handleTrustedContactClick() {
      this.track('trusted_contact_link', 'interaction');
      const url = this.pd_content.rc_trusted_contact_pdf ? this.pd_content.rc_trusted_contact_pdf.url : '';
      this.openWindow(url, 'documentwindow', '1024', '500');
    },
    handleHouseholdingOfShareholderClick() {
      this.track('householding_of_shareholde_link', 'interaction');
      const url = this.pd_content.rc_householding_of_shareholder_pdf ? this.pd_content.rc_householding_of_shareholder_pdf.url : '';
      this.openWindow(url, 'documentwindow', '1024', '500');
    },
    handleElectronicDelivery() {
      this.track('electronic_delivery_agreement_link', 'interaction');
      const url = this.pd_content.rc_electronic_delivery_agreement_pdf ? this.pd_content.rc_electronic_delivery_agreement_pdf.url : '';
      this.openWindow(url, 'documentwindow', '1024', '500');
    },
    handleProspectusClick(coreSymbol) {
      this.track(`${coreSymbol}_prospectus_link`, 'interaction');
      this.openWindow(this.prospectusUrl, 'documentwindow', '1024', '500');
    },
    openModal() {
      document.querySelector('#prospectus-modal').setAttribute('active', 'true');
    },
    closeModal() {
      document.querySelector('#prospectus-modal').setAttribute('active', 'false');
    },
    handleManagedProspectusClick() {
      this.openModal();
    },
    openProspectusWindow(i) {
      const firstIndObj = this.pd_prospectuses.models[0].modelHoldings[i].finstAllocation.finstId;
      const { cusip } = firstIndObj;
      const url = this.pd_envConfig.modelHoldingsURL + cusip;
      this.closeModal();
      this.openWindow(url, 'documentwindow', '650', '650');
    },
    handlePredisputeArbitration() {
      this.track('pre_dispute_clause_link', 'interaction');
      const url = this.pd_content.rc_predispute_arbitration_clause_pdf ? this.pd_content.rc_predispute_arbitration_clause_pdf.url : '';
      this.openWindow(url, 'documentwindow', '1024', '500');
    },
    handleProgramBankClick() {
      this.track('program_bank_link', 'interaction');
      const url = this.pd_content.rc_core_position_program_bank_pdf ? this.pd_content.rc_core_position_program_bank_pdf.url : '';
      this.openWindow(url, 'documentwindow', '1024', '500');
    },
    handleFdicClick() {
      this.track('fdic_link', 'interaction');
      const url = this.pd_content.rc_core_position_fdic_pdf ? this.pd_content.rc_core_position_fdic_pdf.url : '';
      this.openWindow(url, 'documentwindow', '1024', '500');
    },
    handleBillPayClick() {
      this.track('billpay_link', 'interaction');
      const url = this.pd_content.rc_account_feature_billpay_link ? this.pd_content.rc_account_feature_billpay_link.url : '';
      this.openWindow(url, 'documentwindow', '1024', '500');
    },
    handleDebitClick() {
      this.track('debit_link', 'interaction');
      const url = this.pd_content.rc_account_feature_debit_link ? this.pd_content.rc_account_feature_debit_link.url : '';
      this.openWindow(url, 'documentwindow', '1024', '500');
    },
    handleCheckWritingClick() {
      this.track('checkwriting_link', 'interaction');
      const url = this.pd_content.rc_account_feature_checkwriting_link ? this.pd_content.rc_account_feature_checkwriting_link.url : '';
      this.openWindow(this.replaceText(url, '&amp;', '&'), 'documentwindow', '1024', '500');
    },
  },
};
</script>

<style scoped>
@media not print {
  .pd-spray-details {
    display:none;
  }
}
.rc-footnote {
  text-align: center;
  font-size: 1em;
}
</style>
